import { fontSize, size } from '@styles/theme'
import styled from '@emotion/styled'
import RegisteredIndicator from '@elements/RegisteredIndicator'
import StatusIndicator from '@elements/StatusIndicator'
import { PiInfo } from 'react-icons/pi'
import { Dialog } from '@mui/material'

const DialogTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 0.5px solid rgba(255,255,255, 0.25);
  padding: ${size(2)};
  margin: 0;
`

const DialogDescription = styled.span`
  padding: ${size(1.5)} ${size(3)};
  font-size: ${fontSize('xs')};
  line-height: ${size(2.25)};
  text-align: center;
`

const SectionHeader = styled.div`
  background-color: rgba(255,255,255,0.075);
  padding: ${size(2)};
  font-size: ${fontSize('sm')};
  border-top: 0.5px solid rgba(255,255,255, 0.25);
  border-bottom: 0.5px solid rgba(255,255,255, 0.25);
`

const HeaderTitle = styled.b`
  font-weight: 500;
  letter-spacing: ${size(0.1)};
`

const HeaderText = styled.span`
  color: lightgrey;
  font-size: ${fontSize('xs')};
`

const HeaderHr = styled.hr`
  border: 0.5px solid rgba(255,255,255, 0.125);
  margin-bottom: ${size(1.5)};
  margin-top: ${size(0.5)};
`

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size(1)};
  padding: ${size(1)};
`

const ListItem = styled.div`
  display: flex;
  align-items: center;
  min-height: ${size(8)};
  padding: ${size(1.5)};
  gap: ${size(2)};
  background-color: rgba(255,255,255,0.05);
  border-radius: 12px;
`

const LiText = styled.span`
  font-size: ${fontSize('xs')};
  line-height: ${size(2)};
`

const slotProps = {
  paper: {
    style: {
      width: '90vw',
      height: '65vh',
      position: 'relative',
      backgroundColor: '#141414',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      border: '0.5px solid rgba(255,255,255, 0.25)',
      overflowY: 'scroll',
    }
  }
}

const NearbyLegendDialog = ({ isOpen, setClosed }) => {
  return(
    <Dialog
      slotProps={slotProps}
      open={isOpen}
      onClose={setClosed}>
      <DialogTitle><PiInfo size={size(2.25)} />&nbsp;Map Legend</DialogTitle>
      <DialogDescription>
        Use this to explain the meaning of elements on the nearby (map & grid) screens.
      </DialogDescription>
      <SectionHeader>
        <HeaderTitle>LOOKING STATUS</HeaderTitle>
        <HeaderHr />
        <HeaderText>
          A temporary status that tells people what you're looking for <i>at this moment</i>.
        </HeaderText>
      </SectionHeader>
      <SectionList>
        <ListItem>
          <StatusIndicator
            status='browsing'
            size={size(4)}
          />
          <LiText>
            <b>Browsing</b>: <u>The <i>default</i> status</u> - indicates not looking for anything in particular. This <i>will not</i> show an icon on the map or grid.
          </LiText>
        </ListItem>
        <ListItem>
          <StatusIndicator
            status='right_now'
            size={size(4)}
          />
          <LiText>
            <b>Right Now</b>: Looking for sex/intimacy or a hookup <i>(not platonic)</i>
          </LiText>
        </ListItem>
        <ListItem>
          <StatusIndicator
            status='meetup'
            size={size(4)}
          />
          <LiText>
            <b>Meetup</b>: Wanting to meetup as friends, platonically, without sex.
          </LiText>
        </ListItem>
        <ListItem>
          <StatusIndicator
            status='chatting'
            size={size(4)}
          />
          <LiText>
            <b>Chatting</b>: Here for chatting, but not to meetup up (sexually or otherwise)
          </LiText>
        </ListItem>
        <ListItem>
          <StatusIndicator
            status='romance'
            size={size(4)}
          />
          <LiText>
            <b>Romance</b>: Looking for a romantic date. Ask me out!
          </LiText>
        </ListItem>
      </SectionList>
      <br />
      <SectionHeader>
        <HeaderTitle>USER REGISTRATION</HeaderTitle>
        <HeaderHr />
        <HeaderText>
          Tells you if the user is anonymous, registered, or a subscribed member.
        </HeaderText>
      </SectionHeader>
      <SectionList>
        <ListItem>
          <RegisteredIndicator user={{ isAnonymous: true }} size={size(3.5)} />
          <LiText>
            <b>Anonymous</b>: <i>Temporary</i> users that wish to participate without leaving a large digital footprint.
          </LiText>
        </ListItem>
        <ListItem>
          <RegisteredIndicator user={{ isAnonymous: false }} size={size(3.5)} />
          <LiText>
            <b>Registered</b>: Basic users that have full access to the social feeds and nearby screens.
          </LiText>
        </ListItem>
        <ListItem>
          <RegisteredIndicator user={{ isSubscribed: true }} size={size(3.5)} />
          <LiText>
            <b>Subscribed</b>: Members with an active subscription to extra features. They support the app ❤️
          </LiText>
        </ListItem>
      </SectionList>
      <br />
      <SectionHeader>
        <HeaderTitle>MAP EVENTS</HeaderTitle>
        <HeaderHr />
        <HeaderText>
          Various things you might see happen on the map as you browse profiles.
        </HeaderText>
      </SectionHeader>
      <SectionList>
        <ListItem>
          <LiText>
            <b><u>Pulsing Profiles</u></b>: A profile that pulses, or gets briefly bigger and small again, indicates a user is clicking through nearby profiles and is actively engaged.
          </LiText>
        </ListItem>
        <ListItem>
          <LiText>
            <b><u>Appearing Profiles</u></b>: Shows that a user has just logged on or unhid themselves.
          </LiText>
        </ListItem>
        <ListItem>
          <LiText>
            <b><u>Disappearing Profiles</u></b>: Typically from users hiding themselves, but they could also have destroyed their account or used their blocks.
          </LiText>
        </ListItem>
        <ListItem>
          <LiText>
            <b><u>Changing Profile Information</u></b>: When users update their nearby information (and looking status) it will propogate to other nearby users in real time.
          </LiText>
        </ListItem>
      </SectionList>
      <br />
    </Dialog>
  )
}

export default NearbyLegendDialog
